import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import { ProductCard } from 'components';

import { useIsAuthenticated } from 'hooks';
import { t } from 'ttag';

import gtmPusher from 'utils/gtmPusher';

import Modal from 'js/Account/Login/Modals/Modal';

export default function ProductGrid({
  products,
  search,
  categoryTitle,
  isLoading
}) {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { isAuthenticated } = useIsAuthenticated();

  /**
   * Google Tag Manager Pusher
   */
  useEffect(() => {
    if (products && products.length > 0) {
      const eComData = {
        currencyCode: products[0]?.currency || 'N/A',
        event: 'impressionView',
        eventAction: 'impression view',
        eventCategory: 'ecommerce',
        eventLabel: 'list',
        eventNonInteraction: true,
        eventValue: 0,
        pageType: window?.pageType || undefined,
        list:
          window?.pageType === 'search' && searchQuery
            ? `search: ${searchQuery}`
            : `category page: ${categoryTitle}`
      };

      const productsData = products.map((product, index) => {
        return {
          ...product,
          product_id: product.id,
          product: product.name,
          quantity: 1,
          unit_net: product.prices?.min_price || undefined,
          size: null,
          variant: '',
          position: index
        };
      });

      gtmPusher(eComData, productsData);
    }
  }, [products]);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    if (searchParams.has('q')) {
      const query = searchParams.get('q');
      setSearchQuery(query);
    }
  }, [search]);

  return (
    <>
      <ul
        className="m-0 grid min-h-[50vh] list-none auto-cols-fr auto-rows-fr grid-cols-2 gap-8 p-0 sm:grid-cols-3 sm:gap-8 md:grid-cols-4 md:gap-x-8 md:gap-y-12"
        data-testid="products"
      >
        {products.map(product => (
          <ProductCard
            key={product.id}
            id={product.id}
            url={`${product.relative_url}${
              searchQuery ? `?search_query=${searchQuery}` : ''
            }`}
            image={product.image}
            imageAlt={product.name}
            labels={product.labels}
            title={product.name}
            min_price={product.prices?.min_price}
            max_price={product.prices?.max_price}
            rrp={product.prices?.recommended_retail_price}
            discountPercentage={product.prices?.discount_percentage || 0}
            currency={product.prices?.currency || ''}
            sizes={product.stock}
            isAuthenticated={isAuthenticated}
            isExclusive={product.is_exclusive}
            isFavourite={product.is_favorite}
            setShowLoginModal={setShowLoginModal}
            disabled={isLoading}
          />
        ))}
      </ul>
      <Sentry.ErrorBoundary fallback={null}>
        <Modal
          isOpen={showLoginModal}
          onClose={() => {
            setShowLoginModal(false);
          }}
          modal="login"
          loginHeading={t`Please log in to add products to your favorite list.`}
          signupSource="plp_favorite"
        />
      </Sentry.ErrorBoundary>
    </>
  );
}

ProductGrid.propTypes = {
  /**
   * The Product Data to display in the Grid.
   */
  // eslint-disable-next-line react/forbid-prop-types
  products: PropTypes.arrayOf(PropTypes.object),
  /**
   * The Search Query of the current Page.
   */
  search: PropTypes.string.isRequired,
  /**
   * The Title of the Category.
   */
  categoryTitle: PropTypes.string.isRequired,
  /**
   * Determines if the Product Data is being loaded.
   */
  isLoading: PropTypes.bool.isRequired
};

ProductGrid.defaultProps = {
  products: undefined
};
